<template>
  <div class="comparison-pages-card">
    <div
      v-if="loading"
      class="loading-state">
      <i class="fas fa-spinner fa-spin mr-2" />
      Loading comparison pages...
    </div>

    <div
      v-else
      class="space-y-4">
      <!-- Existing Pages -->
      <div
        v-if="comparisonPages.length > 0"
        class="existing-pages">
        <div class="grid gap-3">
          <div
            v-for="page in comparisonPages"
            :key="page.id"
            class="page-item">
            <div class="flex items-center justify-between">
              <a
                :href="page.linkUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="page-link">
                <i class="fas fa-external-link-alt mr-2" />
                {{ page.linkTitle }}
              </a>
              <div class="flex items-center gap-2">
                <button
                  v-if="!page.editing"
                  class="btn-icon"
                  title="Edit"
                  @click="startEdit(page)">
                  <i class="fas fa-pencil-alt" />
                </button>
                <button
                  v-if="!page.editing"
                  class="btn-icon text-red-600 hover:text-red-700"
                  title="Delete"
                  @click="deletePage(page)">
                  <i class="fas fa-trash-alt" />
                </button>
              </div>
            </div>

            <!-- Edit Form -->
            <div
              v-if="page.editing"
              class="edit-form mt-3">
              <div class="grid gap-3">
                <div>
                  <label class="form-label">Link Title</label>
                  <input
                    v-model="page.editTitle"
                    type="text"
                    class="form-input"
                    placeholder="e.g., Q1 2024 Comparison">
                </div>
                <div>
                  <label class="form-label">Link URL</label>
                  <input
                    v-model="page.editUrl"
                    type="url"
                    class="form-input"
                    placeholder="https://example.com/comparison/q1-2024">
                </div>
                <div class="flex gap-2">
                  <button
                    class="btn btn-primary"
                    :disabled="!page.editTitle || !page.editUrl"
                    @click="saveEdit(page)">
                    <i class="fas fa-check mr-1" />
                    Save
                  </button>
                  <button
                    class="btn btn-secondary"
                    @click="cancelEdit(page)">
                    <i class="fas fa-times mr-1" />
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Pages Message -->
      <div
        v-else
        class="empty-state">
        <i class="fas fa-chart-bar text-3xl text-gray-400 mb-2" />
        <p class="text-gray-600">
          No comparison pages yet
        </p>
      </div>

      <!-- Add New Page Form -->
      <div class="add-new-section">
        <button
          v-if="!showNewForm"
          class="btn btn-primary w-full"
          @click="showNewForm = true">
          <i class="fas fa-plus mr-2" />
          Add Comparison Page
        </button>

        <div
          v-else
          class="new-page-form">
          <h4 class="font-medium text-gray-900 mb-3">
            New Comparison Page
          </h4>
          <div class="grid gap-3">
            <div>
              <label class="form-label">Link Title</label>
              <input
                v-model="newPage.linkTitle"
                type="text"
                class="form-input"
                placeholder="e.g., Q1 2024 Comparison">
            </div>
            <div>
              <label class="form-label">Link URL</label>
              <input
                v-model="newPage.linkUrl"
                type="url"
                class="form-input"
                placeholder="https://example.com/comparison/q1-2024">
            </div>
            <div class="flex gap-2">
              <button
                class="btn btn-primary"
                :disabled="!newPage.linkTitle || !newPage.linkUrl || saving"
                @click="createPage">
                <i
                  :class="saving ? 'fas fa-spinner fa-spin' : 'fas fa-save'"
                  class="mr-1" />
                {{ saving ? 'Saving...' : 'Save' }}
              </button>
              <button
                class="btn btn-secondary"
                :disabled="saving"
                @click="cancelNew">
                <i class="fas fa-times mr-1" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import mdtoast from '@dmuy/toast'
import axios from '../init/axios'

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  }
})

// State
const loading = ref(true)
const saving = ref(false)
const comparisonPages = ref([])
const showNewForm = ref(false)
const newPage = ref({
  linkTitle: '',
  linkUrl: ''
})

// Methods
const fetchPages = async() => {
  try {
    const response = await axios.get(`/ajax/organizations/${props.organizationId}/comparison_pages`)
    if (response.data && response.data.comparisonPages) {
      comparisonPages.value = response.data.comparisonPages.map((page) => ({
        ...page,
        editing: false,
        editTitle: page.linkTitle,
        editUrl: page.linkUrl
      }))
    } else {
      // If response doesn't have expected structure, just set empty array
      comparisonPages.value = []
    }
  } catch (error) {
    // Only show error if it's not a 404 (which would mean no pages yet)
    if (error.response && error.response.status !== 404) {
      // console.error('Error fetching comparison pages:', error.response || error)
      mdtoast.error('Failed to load comparison pages')
    } else {
      comparisonPages.value = []
    }
  } finally {
    loading.value = false
  }
}

const createPage = async() => {
  if (!newPage.value.linkTitle || !newPage.value.linkUrl) return

  saving.value = true
  try {
    const response = await axios.post(
      `/ajax/organizations/${props.organizationId}/comparison_pages`,
      { comparisonPage: newPage.value }
    )

    if (response.data.success) {
      comparisonPages.value.unshift({
        ...response.data.comparisonPage,
        editing: false,
        editTitle: response.data.comparisonPage.linkTitle,
        editUrl: response.data.comparisonPage.linkUrl
      })
      newPage.value = { linkTitle: '', linkUrl: '' }
      showNewForm.value = false
      mdtoast.success('Comparison page added successfully')
    }
  } catch (error) {
    // console.error('Error creating comparison page:', error)
    mdtoast.error('Failed to create comparison page')
  } finally {
    saving.value = false
  }
}

const startEdit = (page) => {
  page.editing = true
  page.editTitle = page.linkTitle
  page.editUrl = page.linkUrl
}

const cancelEdit = (page) => {
  page.editing = false
  page.editTitle = page.linkTitle
  page.editUrl = page.linkUrl
}

const saveEdit = async(page) => {
  if (!page.editTitle || !page.editUrl) return

  try {
    const response = await axios.patch(
      `/ajax/organizations/${props.organizationId}/comparison_pages/${page.id}`,
      {
        comparisonPage: {
          linkTitle: page.editTitle,
          linkUrl: page.editUrl
        }
      }
    )

    if (response.data.success) {
      page.linkTitle = page.editTitle
      page.linkUrl = page.editUrl
      page.editing = false
      mdtoast.success('Comparison page updated')
    }
  } catch (error) {
    // console.error('Error updating comparison page:', error)
    mdtoast.error('Failed to update comparison page')
  }
}

const deletePage = async(page) => {
  if (!window.confirm('Are you sure you want to delete this comparison page?')) return

  try {
    const response = await axios.delete(`/ajax/organizations/${props.organizationId}/comparison_pages/${page.id}`)

    if (response.data.success) {
      const index = comparisonPages.value.findIndex((p) => p.id === page.id)
      if (index > -1) {
        comparisonPages.value.splice(index, 1)
      }
      mdtoast.success('Comparison page deleted')
    }
  } catch (error) {
    // console.error('Error deleting comparison page:', error)
    mdtoast.error('Failed to delete comparison page')
  }
}

const cancelNew = () => {
  showNewForm.value = false
  newPage.value = { linkTitle: '', linkUrl: '' }
}

// Lifecycle
onMounted(() => {
  fetchPages()
})
</script>

<style scoped>
.comparison-pages-card {
  @apply p-4;
}

.loading-state {
  @apply text-center py-8 text-gray-600;
}

.empty-state {
  @apply text-center py-8;
}

.page-item {
  @apply bg-gray-50 rounded-lg p-3 border border-gray-200;
}

.page-link {
  @apply text-blue-600 hover:text-blue-800 font-medium flex items-center;
}

.btn-icon {
  @apply p-1.5 text-gray-600 hover:text-gray-800 transition-colors;
}

.edit-form {
  @apply border-t border-gray-200 pt-3;
}

.new-page-form {
  @apply bg-blue-50 rounded-lg p-4 border border-blue-200;
}

.form-label {
  @apply block text-sm font-medium text-gray-700 mb-1;
}

.form-input {
  @apply w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm
         focus:ring-indigo-500 focus:border-indigo-500;
}

.btn {
  @apply px-4 py-2 text-sm font-medium rounded-md focus:outline-none
         focus:ring-2 focus:ring-offset-2 transition-colors;
}

.btn-primary {
  @apply bg-indigo-600 text-white hover:bg-indigo-700
         focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed;
}

.btn-secondary {
  @apply bg-white text-gray-700 border border-gray-300
         hover:bg-gray-50 focus:ring-indigo-500;
}

.add-new-section {
  @apply border-t border-gray-200 pt-4 mt-4;
}
</style>
